import React, { useState, useEffect, useRef } from "react";
import AuthButton from "../components/AuthButton";
import { apiFetch } from "../api";
import { ArrowLeft, Loader2, Menu, Share2, X } from "lucide-react";
import ExampleDownloadButton from "../components/ExampleDownloadButton";
import { toast } from "react-hot-toast";
import { fetchPlanFileBlob, openPlanDownload, requestPlanDownloadPath } from "../utils/planDownload";

/**
 * Точная верстка титульного листа.
 * - A4 (~794x1123 @96dpi), serif, текст 18px
 * - Поля ввода с рамкой; у подписи — просто линия (не поле)
 * - Добавлен отступ над заголовком
 */
export default function TitlePage({
  user,
  plan,
  planUuid,
  status,
  paid,
  onBack,
  onLogin,
  onAccount,
  onSaved,
  onBuy,
  onShareSite,
}) {
  const [form, setForm] = useState({
    author: plan?.author || user?.firstName || "",
    projectCost: plan?.projectCostUsers || plan?.projectCost || "",
    projectDuration: plan?.projectDuration || "",
    paybackPeriod: plan?.paybackPeriod || "",
    branchAddress: plan?.branchAddress || "",
    phone: plan?.phone || "",
    email: plan?.email || user?.email || "",
    titleDate: plan?.titleDate || "",
  });
  const initialFormRef = useRef(form);
  useEffect(() => {
    document.title = "СоцПлан | Титульный лист";
  }, []);
  useEffect(() => {
    const newForm = {
      author: plan?.author || user?.firstName || "",
      projectCost: plan?.projectCostUsers || plan?.projectCost || "",
      projectDuration: plan?.projectDuration || "",
      paybackPeriod: plan?.paybackPeriod || "",
      branchAddress: plan?.branchAddress || "",
      phone: plan?.phone || "",
      email: plan?.email || user?.email || "",
      titleDate: plan?.titleDate || "",
    };
    setForm(newForm);
    initialFormRef.current = newForm;
  }, [plan, user]);
  const [saving, setSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateInputRef = useRef(null);
  const [showDownload, setShowDownload] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const disabled = status === "PROCESSING";
  const [planPrice, setPlanPrice] = useState(1000);

  useEffect(() => {
    if (showDatePicker && dateInputRef.current) {
      dateInputRef.current.showPicker?.();
    }
  }, [showDatePicker]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(min-width: 768px)");
    const handler = (event) => {
      if (event.matches) {
        setMobileMenuOpen(false);
      }
    };
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, []);

  // Load dynamic price from API
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/config', { silent: true });
        if (res.ok) {
          const data = await res.json();
          if (typeof data.planPrice === 'number') setPlanPrice(data.planPrice);
        }
      } catch {}
    })();
  }, []);

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === "titleDate") {
      const digits = value.replace(/\D/g, "").slice(0, 8);
      value = "";
      if (digits.length > 0) {
        value += digits.slice(0, 2);
        if (digits.length >= 3) {
          value += "." + digits.slice(2, 4);
        }
        if (digits.length >= 5) {
          value += "." + digits.slice(4, 8);
        }
      }
    }
    setForm({ ...form, [field]: value });
  };

  const contractFunds = plan?.contractFunds ?? 350000;
  const personalFunds = plan?.ownFunds || 0;
  const defaultCost = form.projectCost || `${contractFunds + personalFunds} руб.`;
  const hasChanges = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const handleSave = async () => {
    if (!planUuid) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/plan/${planUuid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          author: form.author,
          projectCostUsers: form.projectCost,
          projectDurationUsers: form.projectDuration,
          paybackPeriodUsers: form.paybackPeriod,
          branchAddress: form.branchAddress,
          phone: form.phone,
          email: form.email,
          titleDate: form.titleDate,
        }),
        planUuid,
      });
      if (res.ok) {
        const data = await res.json();
        onSaved?.(data);
        toast.success("Бизнес-план сохранен");
      }
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (type) => {
    if (!planUuid) return;
    setDownloading(type);
    try {
      const path = await requestPlanDownloadPath(planUuid, type);
      if (path) {
        openPlanDownload(path);
        setShowDownload(false);
        return;
      }
      const blob = await fetchPlanFileBlob(planUuid, type);
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `plan_${planUuid}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowDownload(false);
    } finally {
      setDownloading(null);
    }
  };

  const menuItems = [
    { title: "Титульный лист" },
    { title: "Резюме проекта", key: "summary" },
    { title: "Описание проекта/продукта/услуги", key: "description" },
    { title: "Маркетинговый анализ", key: "marketAnalysis" },
    { title: "Финансовые показатели и прогнозы", key: "financialPlan" },
    { title: "Анализ рисков", key: "risks" },
    { title: "Социальное влияние", key: "socialImpact" },
  ];
  const hasMenuAction = (item) => Boolean(item.key || item.onClick);

  const handleMenuClick = (key) => {
    if (onBack) onBack(key);
  };

  const handleMenuItem = (item, closeAfter = false) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.key) {
      handleMenuClick(item.key);
    }
    if (closeAfter) {
      setMobileMenuOpen(false);
    }
  };

  const canShowActions = !["PROCESSING", "ERROR"].includes(status);

  const renderSaveButton = () => {
    if (!hasChanges) return null;
    return (
      <button
        onClick={handleSave}
        disabled={saving}
        className="h-11 rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold shadow hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? "Сохранение…" : "Сохранить"}
      </button>
    );
  };

  return (
      <div className="min-h-screen bg-slate-50">
        {/* Верхняя панель (скрывается при печати) */}
        <div className="sticky sticky-safe-top z-30 bg-white/80 backdrop-blur print:hidden">
          <header className="border-b bg-white/80 backdrop-blur">
            <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                  aria-label="Открыть меню"
                  aria-expanded={mobileMenuOpen}
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onBack?.();
                    }}
                    className="font-medium text-slate-700 hover:text-slate-900"
                  >
                    Черновик
                  </button>
                  <span className="text-slate-400">→</span>
                  <span className="font-semibold text-slate-900">Титульный лист</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {onShareSite && (
                  <button
                    type="button"
                    onClick={onShareSite}
                    aria-label="Поделиться сайтом"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs md:text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
                  >
                    <Share2 className="h-4 w-4" />
                    <span className="hidden md:inline">Поделиться</span>
                  </button>
                )}
                <AuthButton user={user} onLogin={onLogin} onAccount={onAccount} />
              </div>
            </div>
          </header>
          {mobileMenuOpen && (
            <div className="md:hidden border-b border-slate-200 bg-white px-4 py-3 print:hidden">
              <div className="text-sm font-semibold text-slate-900">Предпросмотр</div>
              <div className="mt-2 space-y-1">
                {menuItems.map((item, index) => (
                  <div key={index}>
                    {hasMenuAction(item) ? (
                      <button
                        type="button"
                        onClick={() => handleMenuItem(item, true)}
                        className="block w-full rounded-md px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                      >
                        {item.title}
                      </button>
                    ) : (
                      <div className="px-2 py-2 text-sm text-slate-700">{item.title}</div>
                    )}
                  </div>
                ))}
              </div>
              {onShareSite && (
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onShareSite();
                  }}
                  className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:text-slate-900"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Поделиться
                </button>
              )}
            </div>
          )}
        </div>

        {/* Макет с боковым меню */}
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 py-5 md:grid-cols-12 md:gap-6 md:py-6">
          {/* Sidebar */}
          <aside className="hidden md:col-span-3 md:block md:sticky md:top-[4.5rem] md:self-start print:hidden">
            <div className="rounded-xl border bg-white p-3 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto">
              <div className="mb-2 text-sm font-semibold text-slate-900">Предпросмотр</div>
              {menuItems.map((t, i) => (
                <div
                  key={i}
                  onClick={hasMenuAction(t) ? () => handleMenuItem(t) : undefined}
                  className={`py-2 text-sm text-slate-700 ${
                    hasMenuAction(t)
                      ? "cursor-pointer hover:text-slate-900"
                      : "cursor-default font-semibold"
                  }`}
                >
                  {t.title}
                </div>
              ))}
            </div>
          </aside>

          {/* Content */}
          <main className="col-span-12 md:col-span-9">
            <div className="mb-4 print:hidden">
              <button
                type="button"
                onClick={() => onBack?.()}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Назад к разделам
              </button>
            </div>
            <div className="overflow-x-auto rounded-xl border bg-white p-4 text-[15px] font-sans text-slate-900 shadow-sm print:border-0 print:p-0 print:shadow-none print:overflow-visible">
              {/* Полотно A4 */}
              <div className="mx-auto my-8 w-full max-w-[794px] min-h-[1123px] bg-white px-6 py-8 print:my-0 print:px-[48px] print:py-[40px] md:px-[48px] md:py-[40px]">
                {/* Заголовки (добавлен отступ сверху) */}
                <div className="mt-[40px] text-center font-serif">
                  <div className="text-[28px] font-bold leading-tight">БИЗНЕС-ПЛАН</div>
                  <div className="mt-[8px] leading-relaxed text-[18px]">для социального контракта</div>
                </div>

                {/* Первый блок */}
                <div className="mt-[120px] space-y-[18px] leading-[1.7]">
                  <div className="flex flex-wrap items-center">
                    <span className="mr-3">Автор:</span>
                    <input
                        type="text"
                        value={form.author}
                        onChange={handleChange("author")}
                        placeholder="ФИО"
                        className="h-[36px] w-[420px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex flex-wrap items-center">
                    <span className="mr-3">Сметная стоимость бизнес-проекта:</span>
                    <input
                        type="text"
                        value={form.projectCost || defaultCost}
                        onChange={handleChange("projectCost")}
                        placeholder="350000 руб."
                        disabled={disabled}
                        className="h-[36px] w-[320px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex flex-wrap items-center">
                    <span className="mr-3">Срок реализации бизнес-проекта:</span>
                    {disabled && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <input
                        type="text"
                        value={form.projectDuration}
                        onChange={handleChange("projectDuration")}
                        placeholder="1 год"
                        disabled={disabled}
                        className="h-[36px] w-[220px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex flex-wrap items-center">
                    <span className="mr-3">Срок окупаемости бизнес-проекта:</span>
                    {disabled && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <input
                        type="text"
                        value={form.paybackPeriod}
                        onChange={handleChange("paybackPeriod")}
                        placeholder="6 месяцев"
                        disabled={disabled}
                        className="h-[36px] w-[240px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Второй блок */}
                <div className="mt-[120px] space-y-[18px] leading-[1.7]">
                  <div className="flex flex-wrap items-center">
                    <span className="mr-3">Адрес регистрации физлица:</span>
                    <input
                        type="text"
                        value={form.branchAddress}
                        onChange={handleChange("branchAddress")}
                        placeholder="Адрес регистрации"
                        className="h-[36px] w-[420px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex flex-wrap items-center">
                    <span className="mr-3">Контактный телефон:</span>
                    <input
                        type="text"
                        value={form.phone}
                        onChange={handleChange("phone")}
                        placeholder="+7 999 999 99 99"
                        className="h-[36px] w-[260px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex flex-wrap items-center">
                    <span className="mr-3">Электронная почта:</span>
                    <input
                        type="email"
                        value={form.email}
                        onChange={handleChange("email")}
                        placeholder="email@example.com"
                        className="h-[36px] w-[320px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                </div>

                  {/* Низ страницы: убрано поле возле подписи, оставлена линия */}
                  <div className="mt-[160px] flex items-end justify-between">
                    <div className="flex items-center">
                      <span className="mr-3 lowercase">дата</span>
                      <input
                          type="text"
                          value={form.titleDate}
                          onClick={() => setShowDatePicker(true)}
                          readOnly
                          placeholder="дд.мм.гггг"
                          className="h-[36px] w-[160px] border border-slate-400 rounded bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 cursor-pointer"
                      />
                    </div>
                  </div>

                </div>
            </div>
            {canShowActions && (
              <div className="sticky bottom-4 z-30 mt-4 flex flex-wrap items-center justify-end gap-2 print:hidden">
                {renderSaveButton()}
                {!paid ? (
                  <>
                    <ExampleDownloadButton paid={paid} />
                    <button
                      onClick={onBuy}
                      className="h-11 rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold shadow hover:bg-blue-700"
                    >
                      {`Купить бизнес‑план за ${new Intl.NumberFormat('ru-RU').format(planPrice)} ₽`}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowDownload(true)}
                    className="h-11 rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold shadow hover:bg-blue-700"
                  >
                    Скачать
                  </button>
                )}
              </div>
            )}
            </main>
            </div>
          {showDatePicker && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowDatePicker(false)}
          >
            <div
              className="bg-white p-4 rounded shadow"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={dateInputRef}
                type="date"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const formatted = new Date(value).toLocaleDateString("ru-RU");
                    setForm({ ...form, titleDate: formatted });
                    setShowDatePicker(false);
                  }
                }}
                className="border rounded px-2 py-1"
              />
            </div>
          </div>
        )}
      {showDownload && paid && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl relative">
            <button
              onClick={() => setShowDownload(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="text-lg font-semibold">Скачать файл</div>
            {status === "PROCESSING" && (
              <p className="mt-2 text-sm text-slate-600">
                Генерация еще в процессе. Для скачивания файлов дождитесь завершения
              </p>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={() => downloadFile('pdf')}
                disabled={status === "PROCESSING" || downloading === 'pdf'}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {status === "PROCESSING" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> PDF
                  </>
                ) : downloading === 'pdf' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'PDF'
                )}
              </button>
              <button
                onClick={() => downloadFile('docx')}
                disabled={status === "PROCESSING" || downloading === 'docx'}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {status === "PROCESSING" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> DOCX
                  </>
                ) : downloading === 'docx' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'DOCX'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    );
  }
